import { ReloadOutlined } from '@ant-design/icons';
import { stringCompare } from '@mahawi/eshop-common/src/string-compare';
import { Button, Typography } from 'antd';
import { type ColumnsType } from 'antd/es/table';
import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import Link from '../components/link';
import Table from '../components/table';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { type RootState } from '../reducers';
import { ebayMarketplacesLoad } from '../reducers/ebay/reducer';

interface DataType {
  key: string;
  name: string;
  isActive: boolean;
  defaultCategoryId?: string;
  languageTypeCode?: string;
  ebayLanguage: string | null;
}

const { Text } = Typography;

function EbayMarketplacesContainer(): React.ReactElement {
  const dispatch = useAppDispatch();
  const ebayMarketplaces = useSelector((s: RootState) => s.Ebay.marketplaces);
  const ebayInProcess = useSelector((s: RootState) => s.Ebay.inProcess);

  useEffect(() => {
    dispatch(ebayMarketplacesLoad());
  }, [dispatch]);

  const dataSource = useMemo<DataType[]>(
    () =>
      ebayMarketplaces
        .map((marketplace): DataType => ({
          key: marketplace.code,
          name: marketplace.name,
          isActive: marketplace.isActive,
          defaultCategoryId: marketplace.defaultCategoryId,
          languageTypeCode: marketplace.languageType.code,
          ebayLanguage: marketplace.ebayLanguage,
        }))
        .sort((a, b) => stringCompare(a.name, b.name)),
    [ebayMarketplaces],
  );

  const columns = useMemo<ColumnsType<object>>(
    () => [
      {
        title: 'Name',
        dataIndex: 'name',
        sorter: {
          compare: (a: DataType, b: DataType) => stringCompare(a.name, b.name),
        },
        filterSearch: true,
        filters: [...new Set(dataSource.map(({ name }) => name))]
          .map((name) => ({
            text: name,
            value: name,
          }))
          .sort((a, b) => stringCompare(a.text, b.text)),
        onFilter: (value, record: DataType) => record.name === value,
        render: (text: string, record: DataType) => (
          <Link
            to={`/ebay/marketplace/${record.key}`}
            label={text}
            openInNewTab
          />
        ),
      },
      {
        title: 'Is active',
        dataIndex: 'isActive',
        sorter: {
          compare: (a: DataType, b: DataType): number =>
            a.isActive === b.isActive ? 0 : a.isActive ? -1 : 1,
        },
        render: (isActive: boolean) =>
          isActive ? (
            <Text strong type="success">
              Active
            </Text>
          ) : (
            <Text strong type="warning">
              Deactivated
            </Text>
          ),
      },
      {
        title: 'Default category ID',
        dataIndex: 'defaultCategoryId',
        render: (defaultCategoryId: string | null) =>
          defaultCategoryId ? (
            <Text strong>{defaultCategoryId}</Text>
          ) : (
            <Text strong type="danger">
              not set
            </Text>
          ),
      },
      {
        title: 'Language',
        dataIndex: 'ebayLanguage',
        render: (ebayLanguage: string | null) => (
          <Text strong>{ebayLanguage}</Text>
        ),
      },
      {
        title: 'Language type code',
        dataIndex: 'languageTypeCode',
        render: (languageTypeCode: string | undefined) =>
          languageTypeCode ? (
            <Text strong>{languageTypeCode}</Text>
          ) : (
            <Text strong type="danger">
              not set
            </Text>
          ),
      },
    ],
    [dataSource],
  );

  return (
    <>
      <Button
        color="primary"
        onClick={() => {
          dispatch(ebayMarketplacesLoad());
        }}
        disabled={ebayInProcess}
      >
        <ReloadOutlined />
      </Button>

      <Table
        columns={columns}
        dataSource={dataSource}
        isLoading={ebayInProcess}
      />
    </>
  );
}

export default EbayMarketplacesContainer;
